import * as T from './ReportsChatbotConstants';

import type {
    MerchantInfoPayloadInterface,
    MerchantInfoSuccessResponseInterface,
    chatbotStartSessionPayloadInterface,
    InsightBotInitResponse,
    ChatHistoryPayloadInterface,
    ChatHistoryListSuccessResponseInterface,
    ChatHistorySessionInterface,
    SessionResumePayloadInterface,
    ConvMessage,
} from '../../../interface/ReportsChatbotInterface';

// ── Merchant Info ─────────────────────────────────────────────────────────────

export const fetchMerchantInfoRequest = (payload: MerchantInfoPayloadInterface) => ({
    type: T.FETCH_MERCHANT_INFO_REQUEST as typeof T.FETCH_MERCHANT_INFO_REQUEST,
    payload,
});

export const fetchMerchantInfoSuccess = (data: MerchantInfoSuccessResponseInterface) => ({
    type: T.FETCH_MERCHANT_INFO_SUCCESS as typeof T.FETCH_MERCHANT_INFO_SUCCESS,
    payload: data,
});

export const fetchMerchantInfoFailure = (error: string) => ({
    type: T.FETCH_MERCHANT_INFO_FAILURE as typeof T.FETCH_MERCHANT_INFO_FAILURE,
    payload: { error },
});

// ── Session ───────────────────────────────────────────────────────────────────

export const startSessionRequest = (payload: chatbotStartSessionPayloadInterface) => ({
    type: T.START_SESSION_REQUEST as typeof T.START_SESSION_REQUEST,
    payload,
});

export const startSessionSuccess = (data: InsightBotInitResponse) => ({
    type: T.START_SESSION_SUCCESS as typeof T.START_SESSION_SUCCESS,
    payload: data,
});

export const startSessionFailure = (error: string) => ({
    type: T.START_SESSION_FAILURE as typeof T.START_SESSION_FAILURE,
    payload: { error },
});

// ── Chat History ──────────────────────────────────────────────────────────────

export const fetchChatHistoryRequest = (payload: ChatHistoryPayloadInterface) => ({
    type: T.FETCH_CHAT_HISTORY_REQUEST as typeof T.FETCH_CHAT_HISTORY_REQUEST,
    payload,
});

export const fetchChatHistorySuccess = (data: ChatHistoryListSuccessResponseInterface) => ({
    type: T.FETCH_CHAT_HISTORY_SUCCESS as typeof T.FETCH_CHAT_HISTORY_SUCCESS,
    payload: data,
});

export const fetchChatHistoryFailure = (error: string) => ({
    type: T.FETCH_CHAT_HISTORY_FAILURE as typeof T.FETCH_CHAT_HISTORY_FAILURE,
    payload: { error },
});

// ── Resume Session (Recent Insights) ────────────────────────────────────────────

export const resumeSessionRequest = (payload: SessionResumePayloadInterface) => ({
    type: T.RESUME_SESSION_REQUEST as typeof T.RESUME_SESSION_REQUEST,
    payload,
});

export const resumeSessionSuccess = (data: ChatHistorySessionInterface) => ({
    type: T.RESUME_SESSION_SUCCESS as typeof T.RESUME_SESSION_SUCCESS,
    payload: data,
});

export const resumeSessionFailure = (error: string) => ({
    type: T.RESUME_SESSION_FAILURE as typeof T.RESUME_SESSION_FAILURE,
    payload: { error },
});

// ── Query flow ────────────────────────────────────────────────────────────────

export type ChatbotMessageTarget = 'main' | 'resume';

export interface SendQueryPayload {
    query: string;
    sessionId: string;
    turnId: string;
    locationId: string;
    apiFailureResponse: string;
    // Which message list this turn's messages belong to: the main chat panel,
    // or the "Recent Insights" resumed-session live tail. Defaults to 'main'.
    target?: ChatbotMessageTarget;
}

export const sendQueryRequest = (payload: SendQueryPayload) => ({
    type: T.SEND_QUERY_REQUEST as typeof T.SEND_QUERY_REQUEST,
    payload,
});

export const setClassifyLoading = (loading: boolean) => ({
    type: T.CLASSIFY_LOADING_SET as typeof T.CLASSIFY_LOADING_SET,
    payload: loading,
});

export const setMetricsLoading = (loading: boolean) => ({
    type: T.METRICS_LOADING_SET as typeof T.METRICS_LOADING_SET,
    payload: loading,
});

export const setInsightsLoading = (loading: boolean) => ({
    type: T.INSIGHTS_LOADING_SET as typeof T.INSIGHTS_LOADING_SET,
    payload: loading,
});

// ── Retry ─────────────────────────────────────────────────────────────────────

export interface RetryPayload {
    messageId: string;
    sessionId: string;
    locationId: string;
    apiFailureResponse: string;
    target?: ChatbotMessageTarget;
}

export const retryDataRequest = (payload: RetryPayload) => ({
    type: T.RETRY_DATA_REQUEST as typeof T.RETRY_DATA_REQUEST,
    payload,
});

export const retryInsightsRequest = (payload: RetryPayload) => ({
    type: T.RETRY_INSIGHTS_REQUEST as typeof T.RETRY_INSIGHTS_REQUEST,
    payload,
});

// ── Conversation ──────────────────────────────────────────────────────────────

export const addMessage = (message: ConvMessage, target: ChatbotMessageTarget = 'main') => ({
    type: T.CHATBOT_ADD_MESSAGE as typeof T.CHATBOT_ADD_MESSAGE,
    payload: message,
    target,
});

export const updateMessage = (
    payload: { id: string; changes: Record<string, any> },
    target: ChatbotMessageTarget = 'main'
) => ({
    type: T.CHATBOT_UPDATE_MESSAGE as typeof T.CHATBOT_UPDATE_MESSAGE,
    payload,
    target,
});

export const resetConversation = () => ({
    type: T.CHATBOT_RESET_CONVERSATION as typeof T.CHATBOT_RESET_CONVERSATION,
});

export const cancelQueryRequest = () => ({
    type: T.CANCEL_QUERY_REQUEST as typeof T.CANCEL_QUERY_REQUEST,
});

export const cancelLoadingMessages = () => ({
    type: T.CHATBOT_CANCEL_LOADING_MESSAGES as typeof T.CHATBOT_CANCEL_LOADING_MESSAGES,
});

// ── Action union type ─────────────────────────────────────────────────────────

export type ChatbotAction =
    | ReturnType<typeof fetchMerchantInfoRequest>
    | ReturnType<typeof fetchMerchantInfoSuccess>
    | ReturnType<typeof fetchMerchantInfoFailure>
    | ReturnType<typeof startSessionRequest>
    | ReturnType<typeof startSessionSuccess>
    | ReturnType<typeof startSessionFailure>
    | ReturnType<typeof fetchChatHistoryRequest>
    | ReturnType<typeof fetchChatHistorySuccess>
    | ReturnType<typeof fetchChatHistoryFailure>
    | ReturnType<typeof resumeSessionRequest>
    | ReturnType<typeof resumeSessionSuccess>
    | ReturnType<typeof resumeSessionFailure>
    | ReturnType<typeof sendQueryRequest>
    | ReturnType<typeof setClassifyLoading>
    | ReturnType<typeof setMetricsLoading>
    | ReturnType<typeof setInsightsLoading>
    | ReturnType<typeof retryDataRequest>
    | ReturnType<typeof retryInsightsRequest>
    | ReturnType<typeof addMessage>
    | ReturnType<typeof updateMessage>
    | ReturnType<typeof resetConversation>
    | ReturnType<typeof cancelQueryRequest>
    | ReturnType<typeof cancelLoadingMessages>;
